import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Plus, FolderKanban, Search, Trash2, Edit, Loader2,
  MapPin, Calendar, Building2,
} from 'lucide-react';
import { ExportDropdown } from '../shared/PageActions';
import { API, apiErrorMessage } from '../../lib/api';
import axios from 'axios';
import { toast } from 'sonner';

// ── Status badge ─────────────────────────────────────────────────────────────
function ProjectStatusBadge({ status }) {
  const styles = {
    planning:   { bg: '#dbeafe', color: '#1e40af', border: '#3b82f6' },
    active:     { bg: '#d1fae5', color: '#065f46', border: '#10b981' },
    completed:  { bg: '#f3f4f6', color: '#6b7280', border: '#9ca3af' },
    on_hold:    { bg: '#fef3c7', color: '#92400e', border: '#f59e0b' },
    cancelled:  { bg: '#fee2e2', color: '#991b1b', border: '#ef4444' },
  };
  const s = styles[status] || styles.planning;
  const labelMap = { on_hold: 'On Hold', cancelled: 'Cancelled' };
  const label = labelMap[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Planning');
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}33` }}
    >
      {label}
    </span>
  );
}

// ── KPI bar ──────────────────────────────────────────────────────────────────
function KPIBar({ projects }) {
  const planning   = projects.filter(p => p.status === 'planning').length;
  const active     = projects.filter(p => p.status === 'active').length;
  const completed  = projects.filter(p => p.status === 'completed').length;
  const onHold     = projects.filter(p => p.status === 'on_hold').length;
  const cancelled  = projects.filter(p => p.status === 'cancelled').length;

  return (
    <div className="flex flex-wrap gap-3 mb-4">
      {[
        { label: 'Planning',   value: planning,   color: '#3b82f6' },
        { label: 'Active',     value: active,     color: '#10b981' },
        { label: 'Completed',  value: completed,  color: '#6b7280' },
        { label: 'On Hold',    value: onHold,     color: '#f59e0b' },
        { label: 'Cancelled',  value: cancelled,  color: '#ef4444' },
      ].map(kpi => (
        <div
          key={kpi.label}
          className="flex items-center gap-2 px-3 py-2 rounded-md"
          style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}
        >
          <span className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: kpi.color }}>
            {kpi.value}
          </span>
          <span className="text-xs text-[#6b7280]">{kpi.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── New project modal ────────────────────────────────────────────────────────
function NewProjectModal({ open, onClose, onCreated, clients }) {
  const [form, setForm] = useState({
    project_name: '', client_id: '', status: 'planning',
    site_address: '', start_date: '', end_date: '', description: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.project_name.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/projects`, form);
      onCreated(res.data);
      setForm({
        project_name: '', client_id: '', status: 'planning',
        site_address: '', start_date: '', end_date: '', description: '',
      });
      onClose();
    } catch (err) {
      console.error('Failed to create project:', err);
      toast.error(apiErrorMessage(err, 'Could not create the project.'));
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: '#e5e4df' }}>
          <h3 className="text-lg font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            New Project
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <Field label="Project Name *" value={form.project_name} onChange={v => handleChange('project_name', v)} />
          <div>
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">Client</label>
            <select
              value={form.client_id}
              onChange={e => handleChange('client_id', e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
              style={{ borderColor: '#e5e4df' }}
            >
              <option value="">— No Client —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">Status</label>
            <select
              value={form.status}
              onChange={e => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
              style={{ borderColor: '#e5e4df' }}
            >
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <Field label="Site Address" value={form.site_address} onChange={v => handleChange('site_address', v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start Date" value={form.start_date} onChange={v => handleChange('start_date', v)} type="date" />
            <Field label="End Date" value={form.end_date} onChange={v => handleChange('end_date', v)} type="date" />
          </div>
          <Field label="Description" value={form.description} onChange={v => handleChange('description', v)} multiline />
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: '#e5e4df' }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.project_name.trim()}
            className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Project
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border rounded text-sm resize-none"
          style={{ borderColor: '#e5e4df' }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full px-3 py-2 border rounded text-sm"
          style={{ borderColor: '#e5e4df' }}
        />
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ProjectsTab({ onEditProject }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // ── Fetch projects ────────────────────────────────────────────────────────
  const fetchProjects = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (searchQuery) params.set('search', searchQuery);
      const res = await axios.get(`${API}/projects?${params.toString()}`);
      setProjects(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      toast.error(apiErrorMessage(err, 'Could not load projects.'));
    }
  }, [filterStatus, searchQuery]);

  // ── Fetch clients for modal dropdown ──────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/clients`).then(res => setClients(res.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchProjects().finally(() => setLoading(false));
  }, [fetchProjects]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteProject = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Failed to delete project:', err);
      toast.error(apiErrorMessage(err, 'Could not delete the project.'));
    }
  };

  // ── Search debounce ──────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── PDF / Print stubs ────────────────────────────────────────────────────
  const handlePrint = () => window.print();

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* KPI */}
      <KPIBar projects={projects} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border rounded"
              style={{ borderColor: '#e5e4df' }}
            />
          </div>
          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-2 py-1.5 text-sm border rounded"
            style={{ borderColor: '#e5e4df' }}
          >
            <option value="">All Status</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <ExportDropdown onPdfPreview={() => {}} onPdfDownload={() => {}} onPrint={handlePrint} />
          <Button
            onClick={() => setShowNewModal(true)}
            size="sm"
            className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Project</span>
          </Button>
        </div>
      </div>

      {/* List */}
      {projects.length === 0 ? (
        <div className="text-center py-16">
          <FolderKanban className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
          <p className="text-[#6b7280] text-lg mb-2">No projects yet</p>
          <p className="text-[#6b7280]/60 text-sm mb-6">Create your first project to get started</p>
          <Button onClick={() => setShowNewModal(true)} className="bg-[#1a1a2e] text-white hover:bg-[#252545]">
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {/* Desktop header */}
          <div
            className="hidden lg:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
            style={{ gridTemplateColumns: '1fr 140px 100px 140px 100px 100px' }}
          >
            <span>Project</span>
            <span>Client</span>
            <span>Status</span>
            <span>Site</span>
            <span>Start</span>
            <span className="text-right">Actions</span>
          </div>

          {projects.map(p => (
            <div key={p.id}>
              {/* Desktop row */}
              <div
                className="hidden lg:grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
                style={{
                  gridTemplateColumns: '1fr 140px 100px 140px 100px 100px',
                  borderBottom: '1px solid rgba(201,168,76,0.1)',
                }}
                onClick={() => onEditProject(p.id)}
              >
                <div>
                  <span className="text-sm font-semibold text-[#1a1a2e]">{p.project_name}</span>
                  <span className="text-xs font-mono text-[#9ca3af] ml-2">{p.project_no}</span>
                </div>
                <span className="text-sm text-[#6b7280] truncate">{p.client_name || '—'}</span>
                <ProjectStatusBadge status={p.status} />
                <span className="text-xs text-[#6b7280] truncate">{p.site_address || '—'}</span>
                <span className="text-xs text-[#6b7280]">{p.start_date || '—'}</span>
                <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => onEditProject(p.id)} className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete Project</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {p.project_name}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteProject(p.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Mobile card */}
              <div
                className="lg:hidden px-3 py-3 cursor-pointer hover:bg-[#f3f2ef] transition-colors rounded-md"
                style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}
                onClick={() => onEditProject(p.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-sm font-semibold text-[#1a1a2e]">{p.project_name}</span>
                    <span className="text-xs font-mono text-[#9ca3af] ml-1">{p.project_no}</span>
                  </div>
                  <ProjectStatusBadge status={p.status} />
                </div>
                {p.client_name && (
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-[#6b7280]">
                    <Building2 className="w-3 h-3" />
                    {p.client_name}
                  </div>
                )}
                {p.site_address && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-[#9ca3af]">
                    <MapPin className="w-3 h-3" />
                    <span className="truncate">{p.site_address}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-[#6b7280]">
                  {p.start_date && (
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{p.start_date}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New project modal */}
      <NewProjectModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={(newProject) => {
          setProjects(prev => [newProject, ...prev]);
        }}
        clients={clients}
      />
    </>
  );
}
